import { useEffectQueue, type ActiveEffect } from '@features/effect-queue'
import { createTranslator, usePluginI18nStore } from '@features/plugin-i18n'
import { effectRegistry } from '@shared/lib/effect-registry'
import { resolveWidgetPriority } from '@shared/lib/zone-layout'
import { useCallback, useEffect } from 'react'

import { resolveEventZone } from './resolveEventZone'
import { ZonedEffectPortal } from './ZonedEffectPortal'

/**
 * Вспомогательный компонент для неизвестного типа плагина (нет в effectRegistry).
 *
 * ВАЖНО: onDone нельзя вызывать прямо в теле .map() у родителя — это побочный
 * эффект во время рендера (может вызвать setState-в-рендере родителя и
 * непредсказуемые повторные рендеры/предупреждения React). Вместо этого
 * выносим вызов в useEffect отдельного компонента-обёртки.
 */
function UnknownEffect({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    onDone()
  }, [onDone])
  return null
}

/**
 * Рендерит эффекты из очереди. Роутинг по зоне компоновки:
 *  - если backend передал валидную zone в payload (poll/drawing/soundboard —
 *    см. resolveEventZone) — эффект стекуется в соответствующем слоте
 *    ZoneLayoutManager через ZonedEffectPortal (не перекрывает соседей);
 *  - иначе — рендерится напрямую, как раньше (полноэкранные/наслаивающиеся
 *    эффекты custom_effects/donation_alert/fireworks/text_alert, которые сами
 *    позиционируют себя через OverlayEffectSlot или собственный CSS).
 */
export function EffectsLayer() {
  const { active, complete } = useEffectQueue()
  // Подписка на i18n-store: при update_i18n словарь меняется → слой ре-рендерится,
  // активные эффекты получают новый t() БЕЗ пересоздания компонентов.
  const i18nEntries = usePluginI18nStore((s) => s.entries)
  const makeOnDone = useCallback((id: string) => () => complete(id), [complete])

  return (
    <>
      {active.map((effect: ActiveEffect) => {
        const { id, event } = effect
        const plugin = effectRegistry.get(event.type)

        if (!plugin) {
          // Неизвестный тип плагина — разблокируем очередь через useEffect
          // дочернего компонента, а не прямым вызовом здесь (см. UnknownEffect).
          return <UnknownEffect key={id} onDone={makeOnDone(id)} />
        }

        const Component = plugin.component
        const entry = i18nEntries[event.type]
        const i18n = createTranslator(entry?.locale ?? 'en', entry?.dict ?? {})
        const rendered = <Component key={id} event={event} onDone={makeOnDone(id)} i18n={i18n} />

        const zone = resolveEventZone(event.payload)
        if (!zone) return rendered

        // И React key, и widgetId стабильны по event.type (не по id активного эффекта)—
        // иначе каждый новый снимок (напр. обновление опроса) получает новый id →
        // React размонтирует/перемонтирует ZonedEffectPortal → лишний unregisterSlot/
        // registerSlot и мигание виджета в зоне. onDone внутри всё равно закрывает
        // актуальный id (внутри rendered/makeOnDone), очередь работает корректно.
        return (
          <ZonedEffectPortal
            key={event.type}
            widgetId={event.type}
            zone={zone}
            priority={resolveWidgetPriority(event.type)}
          >
            {rendered}
          </ZonedEffectPortal>
        )
      })}
    </>
  )
}
